import React, { useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { IDLE_TIMEOUT_MS, WARNING_BEFORE_MS, LAST_ACTIVITY_KEY } from '../../config/session';

const ACTIVITY_EVENTS = ['mousedown', 'keydown', 'scroll', 'touchstart'];

const warningMinutes = Math.round(WARNING_BEFORE_MS / 60000);
const WARNING_MESSAGE = `Tu sesión se cerrará en ${warningMinutes} minuto${warningMinutes === 1 ? '' : 's'} por inactividad`;

const InactivityGuard: React.FC = () => {
  const { isAuthenticated, logout } = useAuth();
  const { showToast } = useToast();
  const navigate = useNavigate();
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const warningRef = useRef<ReturnType<typeof setTimeout>>();

  const handleLogout = useCallback(() => {
    logout();
    showToast('Sesión cerrada por inactividad', 'info');
    navigate('/login', { replace: true });
  }, [logout, showToast, navigate]);

  const resetTimers = useCallback(() => {
    localStorage.setItem(LAST_ACTIVITY_KEY, String(Date.now()));
    clearTimeout(timeoutRef.current);
    clearTimeout(warningRef.current);
    warningRef.current = setTimeout(() => {
      showToast(WARNING_MESSAGE, 'warning', WARNING_BEFORE_MS);
    }, IDLE_TIMEOUT_MS - WARNING_BEFORE_MS);
    timeoutRef.current = setTimeout(handleLogout, IDLE_TIMEOUT_MS);
  }, [handleLogout, showToast]);

  useEffect(() => {
    if (!isAuthenticated) {
      clearTimeout(timeoutRef.current);
      clearTimeout(warningRef.current);
      return;
    }

    resetTimers();
    ACTIVITY_EVENTS.forEach((evt) => window.addEventListener(evt, resetTimers));

    return () => {
      ACTIVITY_EVENTS.forEach((evt) => window.removeEventListener(evt, resetTimers));
      clearTimeout(timeoutRef.current);
      clearTimeout(warningRef.current);
    };
  }, [isAuthenticated, resetTimers]);

  return null;
};

export default InactivityGuard;
